import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, ArrowRight, Baby, Radar, MapPin, Percent, ShieldCheck } from "lucide-react";
import { api } from "../lib/api";

const CAT_ORDER = ["osobni", "dodavky", "luxusni", "suv", "sportovni", "obytne", "voziky", "elektro"];
const EXTRAS = [
  { icon: Baby, name: "Dětská sedačka", price: 200 },
  { icon: Radar, name: "Antiradar", price: 460 },
  { icon: MapPin, name: "Přistavení vozu k zákazníkovi", priceLabel: "cena dle km" },
];
const INCLUDED = [
  "Havarijní pojištění a povinné ručení",
  "Nájezd 300–500 km/den",
  "Asistenční služba 24/7",
  "Zimní / letní pneumatiky dle sezóny",
  "Dálniční známka pro ČR",
];

export default function Cenik() {
  const [cars, setCars] = useState([]);
  const [cats, setCats] = useState([]);
  useEffect(() => {
    api.get("/cars").then((r) => setCars(r.data)).catch(() => {});
    api.get("/categories").then((r) => setCats(r.data)).catch(() => {});
  }, []);

  const byCat = (slug) => cars.filter((c) => c.category === slug);
  const catName = (slug) => cats.find((c) => c.slug === slug)?.name || slug;

  return (
    <div className="bg-black" data-testid="cenik-page">
      <section className="container-kame pt-14 pb-10">
        <p className="eyebrow">Přehledné ceny</p>
        <h1 className="font-display text-4xl sm:text-5xl text-white uppercase mt-2">Ceník</h1>
        <p className="text-white/60 mt-3 max-w-2xl">
          Všechny ceny jsou uvedeny bez DPH a zahrnují pojištění. Nájezd 300–500 km/den. S délkou pronájmu klesá denní sazba.
        </p>
        <div className="w-16 h-1 bg-[#B58A50] mt-5" />
      </section>

      <section className="container-kame pb-4">
        <div className="card-kame p-5 sm:p-6 border-[#B58A50]/40 bg-[#B58A50]/[0.06] flex items-start gap-4" data-testid="longterm-note">
          <Percent size={26} className="text-[#B58A50] shrink-0 mt-0.5" />
          <div>
            <h3 className="font-display text-lg text-white uppercase">Čím déle, tím levněji</h3>
            <p className="text-white/70 text-sm mt-1">
              Při dlouhodobém pronájmu (např. od 10 nebo 14 dní) získáte u vybraných vozů zvýhodněnou denní sazbu.
              Sleva se automaticky uplatní v rezervaci podle délky pronájmu.
            </p>
          </div>
        </div>
      </section>

      <section className="container-kame pb-12 space-y-10">
        {cats.map((cat) => {
          const slug = cat.slug;
          const list = byCat(slug);
          if (!list.length) return null;
          return (
            <div key={slug} data-testid={`cenik-cat-${slug}`}>
              <h2 className="font-display text-2xl text-white uppercase mb-4">{catName(slug)}</h2>
              <div className="card-kame overflow-hidden">
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b border-[#1f1f1f] text-white/50 text-xs uppercase tracking-wider">
                      <th className="py-3 px-5">Vozidlo</th>
                      <th className="py-3 px-5 hidden sm:table-cell">Motorizace</th>
                      <th className="py-3 px-5 hidden md:table-cell">Palivo</th>
                      <th className="py-3 px-5 text-right">Cena / den</th>
                      <th className="py-3 px-5"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.map((c) => (
                      <tr key={c.id} className="border-b border-[#141414] hover:bg-[#0d0d0d] transition-colors">
                        <td className="py-3 px-5 text-white font-medium">{c.name}</td>
                        <td className="py-3 px-5 text-white/60 hidden sm:table-cell">{c.engine || "—"}</td>
                        <td className="py-3 px-5 text-white/60 hidden md:table-cell">{c.fuel}</td>
                        <td className="py-3 px-5 text-right font-display text-lg text-[#B58A50]">
                          {c.price_per_day.toLocaleString("cs-CZ")} Kč
                        </td>
                        <td className="py-3 px-5 text-right">
                          <Link to={`/rezervace?car=${c.id}`} className="text-[#B58A50] text-xs uppercase tracking-wider hover:underline whitespace-nowrap">
                            Rezervovat →
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}
      </section>

      <section className="bg-[#0a0a0a] border-y border-[#1a1a1a] py-14">
        <div className="container-kame grid md:grid-cols-2 gap-10">
          <div>
            <h2 className="font-display text-2xl text-white uppercase mb-5">V ceně je zahrnuto</h2>
            <ul className="space-y-3">
              {INCLUDED.map((i) => (
                <li key={i} className="flex items-start gap-3 text-white/75 text-sm">
                  <Check size={18} className="text-[#B58A50] mt-0.5 shrink-0" /> {i}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h2 className="font-display text-2xl text-white uppercase mb-5">Doplňkové služby</h2>
            <div className="space-y-3">
              {EXTRAS.map((e) => (
                <div key={e.name} className="card-kame p-4 flex items-center justify-between gap-3">
                  <span className="flex items-center gap-3 text-white/80 text-sm">
                    <e.icon size={18} className="text-[#B58A50]" /> {e.name}
                  </span>
                  <span className="font-display text-[#B58A50] whitespace-nowrap">{e.price ? `${e.price} Kč / den` : e.priceLabel}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="container-kame py-8">
        <div className="card-kame p-5 sm:p-6 flex items-start gap-4" data-testid="deposit-note">
          <ShieldCheck size={26} className="text-[#B58A50] shrink-0 mt-0.5" />
          <div>
            <h3 className="font-display text-lg text-white uppercase">Vratná kauce od 10 000 Kč</h3>
            <p className="text-white/70 text-sm mt-1">
              Při převzetí vozu se skládá vratná kauce (od 10 000 Kč, dle konkrétního vozidla). Kauci vracíme
              v plné výši po vrácení vozu bez poškození. Konkrétní výši najdete u detailu každého vozu.
            </p>
          </div>
        </div>
      </section>

      <section className="container-kame py-14 text-center">
        <h2 className="font-display text-3xl text-white uppercase">Nenašli jste ideální vůz?</h2>
        <p className="text-white/60 mt-3">Ozvěte se nám, rádi vám připravíme nabídku na míru.</p>
        <Link to="/rezervace" className="btn-gold mt-6 inline-flex" data-testid="cenik-reserve-btn">
          Vytvořit rezervaci <ArrowRight size={18} />
        </Link>
      </section>
    </div>
  );
}
